/**
 * Load project context from the applicant API.
 *
 * The portal resolves tenant/project context exclusively from the request
 * hostname -- it NEVER accepts client-provided tenant or project IDs.
 *
 * @module context/context-loader
 */

import type { applicant } from '@noxverify/api-contracts';

type ApplicantContext = applicant.ApplicantContext;
type GetApplicantContextResponse = applicant.GetApplicantContextResponse;

/** Re-export the canonical type so consumers only need this module. */
export type { ApplicantContext };

/**
 * Fetch project context from `GET /v1/applicant/context`.
 *
 * This is the first API call the portal makes on load. The API gateway
 * resolves tenant and project from the request `Host` header, so the
 * portal does not supply any identifiers.
 */
export async function loadProjectContext(
  apiBaseUrl: string,
): Promise<ApplicantContext> {
  let contextUrl = `${apiBaseUrl}/v1/applicant/context`;

  // Dev override: if VITE_PROJECT_SLUG is set, pass it as query param
  const projectSlug = import.meta.env.VITE_PROJECT_SLUG;
  if (projectSlug) {
    contextUrl += `?project_slug=${encodeURIComponent(projectSlug)}`;
  }

  const response = await fetch(contextUrl, {
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`Failed to load project context: ${response.status}`);
  }

  const json = (await response.json()) as GetApplicantContextResponse;
  return json.data;
}
